import { definePreset } from '@primeuix/themes';

import { AgriFinOpsPreset } from './agrifinops-preset';

/**
 * The Figma-derived preset plus a small set of app-level overrides.
 *
 * `agrifinops-preset.ts` is generated and must not be hand-edited, so anything
 * we decide to change on top of the designer's tokens layers in here — one
 * reviewable place that survives `node scripts/build-primeng-preset.mjs`.
 *
 * Keep this file small. Each override should say why it exists, and the durable
 * home for a *design* decision is a Figma token change, not this file.
 */

/**
 * Overlay list rows — dropdowns, autocomplete suggestions, the account menu.
 *
 * The designer's `list.option.padding` is 7px/10.5px, which renders ~28px rows
 * with a 2px gap. In practice that reads as cramped: a four-item year list looks
 * like one block of text rather than four choices. These values give ~40px rows
 * with a 3px gap, so each option is separately clickable and scannable.
 *
 * Set at both levels deliberately:
 *  - `semantic.list` / `semantic.navigation` so any list-style component we add
 *    later inherits the same rhythm;
 *  - `components.*` because the Figma export also pins these per component, and
 *    a component token beats the semantic one it would otherwise reference.
 */
const OPTION_PADDING = '0.625rem 0.75rem'; /* 10px 12px */
const LIST_PADDING = '0.25rem'; /*  4px inset around the options */
const LIST_GAP = '0.1875rem'; /*  3px between options */

export const AgriFinOpsTheme = definePreset(AgriFinOpsPreset, {
  semantic: {
    list: {
      padding: LIST_PADDING,
      gap: LIST_GAP,
      option: {
        padding: OPTION_PADDING
      }
    },
    navigation: {
      list: {
        padding: LIST_PADDING
      },
      item: {
        padding: OPTION_PADDING
      }
    }
  },
  components: {
    select: {
      list: { padding: LIST_PADDING, gap: LIST_GAP },
      option: { padding: OPTION_PADDING },
      emptyMessage: { padding: OPTION_PADDING }
    },
    autocomplete: {
      list: { padding: LIST_PADDING, gap: LIST_GAP },
      option: { padding: OPTION_PADDING },
      emptyMessage: { padding: OPTION_PADDING }
    },
    multiselect: {
      list: { padding: LIST_PADDING, gap: LIST_GAP },
      option: { padding: OPTION_PADDING },
      emptyMessage: { padding: OPTION_PADDING }
    },
    listbox: {
      list: { padding: LIST_PADDING, gap: LIST_GAP },
      option: { padding: OPTION_PADDING },
      emptyMessage: { padding: OPTION_PADDING }
    },
    menu: {
      list: { padding: LIST_PADDING, gap: LIST_GAP },
      item: { padding: OPTION_PADDING }
    }
  }
});
